/**
 * TextTooLongNote: the sentence under an AI tool's box once its text is
 * past the tool's input ceiling (src/lib/aiInputLimits.ts). The box keeps
 * everything pasted; the owning form turns its button off while this shows.
 *
 * The words come from src/lib/errorCopy.ts, the same sentence a server
 * refusal for this box shows, chosen by the box's kind
 * (textTooLongCopyForBox, src/lib/aiTextTooLong.ts): a short box says how
 * many characters to remove, counted from the text in it as it changes,
 * "Who is this for?" says only names go there, and a pasted document is
 * run in halves.
 */

import { type ReactNode } from 'react';
import { FALLBACK_SUPPORT_CONTACT, errorText, type ErrorSurface } from '@/lib/errorCopy';
import { formatMaxChars, textTooLongCopyForBox } from '@/lib/aiTextTooLong';

export { formatMaxChars };

/** True when the trimmed text, counted as the browser counts it, is past the ceiling. */
export function isTextTooLong(text: string, maxChars: number): boolean {
  return text.trim().length > maxChars;
}

export interface TextTooLongNoteProps {
  surface: ErrorSurface;
  maxChars: number;
  /** A short box is shortened, "Who is this for?" takes names, a pasted
   *  document is run in halves. */
  kind: 'shorten' | 'split' | 'recipient';
  /** The box's text as it is now; a short box counts what to remove from it. */
  text: string;
  'data-testid': string;
}

export function TextTooLongNote({ surface, maxChars, kind, text, 'data-testid': testId }: TextTooLongNoteProps): ReactNode {
  const copy = textTooLongCopyForBox({ kind, label: '' }, maxChars, text.trim().length);
  // The sentence names no contact, so the generic one is passed: that keeps
  // the note free of the tenant-config read and usable in any panel.
  return (
    <p className="text-xs text-warm-700 mt-1" data-testid={testId}>
      {errorText(surface, copy.key, FALLBACK_SUPPORT_CONTACT, copy.vars)}
    </p>
  );
}
